// Кэш профиля вошедшего на диске.
//
// Существует ради одного: печать обязана работать без сети (инвариант 3), а из профиля
// берутся ЛИЧНОСТЬ автора этикетки и ТАЙМЗОНА точки, в которой считается срок годности.
//
// Кэш TanStack Query живёт только в памяти — персистера в проекте нет. Значит приложение,
// перезапущенное без сети, спрашивает `/tenants/me/`, получает `NetworkError` и остаётся
// без профиля: печатать некому и не в чём считать даты.
//
// До T-018 этой дыры не было по случайности: личность лежала в Preferences выбранной
// карточкой и перезапуск переживала. Переезд на сессию человека отнял это свойство,
// и вернуть его — работа этого модуля. Дефект не в переезде, а в том, что вместе
// с личностью не переехало её хранение.
//
// На диск идёт ПРОФИЛЬ, но не токен: IndexedDB переживает смену origin и синхронизируется,
// а секрету там не место (решение T-009). Токен живёт в Preferences.

import type { components } from '@/shared/api/generated/schema';

import { isSupportSession } from '@/shared/auth';

import { offlineDb, type SessionCache } from './db';

/**
 * Профиль в форме ответа сервера.
 *
 * Тип берётся из сгенерированной схемы, а не объявляется рядом: расхождение локального
 * типа с контрактом обнаруживается не компилятором, а пустым полем на экране.
 */
export type CachedProfile = components['schemas']['CurrentUser'];

/**
 * Запомнить профиль после успешного ответа сервера.
 *
 * Дрейф часов НЕ перетирается: его ведёт `clock.ts`, и профиль приезжает чаще, чем
 * меряется смещение. Затереть его здесь значило бы обнулять измерение при каждом
 * обновлении профиля.
 *
 * А вот `lastSyncAt` ставится ЗДЕСЬ, и это несущее: профиль, дошедший от сервера,
 * и есть факт связи, от которого считается grace.
 *
 * Раньше его ставил только `rememberDrift`, и на первом входе он не срабатывал:
 * слушатель времени вызывается ДО записи профиля, а без профиля замер выходит рано.
 * Итог — «устройство ещё не связывалось с сервером» и заблокированная печать сразу
 * после успешного входа. Нашёл E2E; юнит-тесты этого не видели, потому что каждый
 * заполнял таблицу сам.
 */
export async function rememberProfile(profile: CachedProfile): Promise<void> {
  // Профиль клиента из саппорт-сессии (T-039) на диск не пишется: после перезапуска
  // платформа вернулась бы в свою сессию с чужим именем и ролью в офлайн-кэше.
  if (isSupportSession()) return;
  const previous = await offlineDb.session.get('current');

  await offlineDb.session.put({
    key: 'current',
    profile,
    clockOffset: previous?.clockOffset ?? 0,
    lastSyncAt: new Date().toISOString(),
  });
}

/**
 * Профиль с диска в форме ответа сервера.
 *
 * Возвращается ровно та форма, что приходит из `/tenants/me/`, — экраны не должны знать,
 * из сети пришёл профиль или с диска. Развилка «а если офлайн» в каждом потребителе
 * означала бы, что забыть её можно в любом из них.
 */
export async function readProfile(): Promise<CachedProfile | null> {
  const cached = await offlineDb.session.get('current');
  return (cached?.profile as CachedProfile | undefined) ?? null;
}

/** Забыть профиль. Зовётся выходом — вместе с очисткой ключей человека. */
export async function forgetProfile(): Promise<void> {
  await offlineDb.session.delete('current');
}

export type { SessionCache };
